import { apiJson, apiStreamOptionalAuth } from '../api';
import { applyEvent, INITIAL_STATE, type FeedV2State } from '../utils/feedsV2';
import { applyForYouV2Event, INITIAL_FOR_YOU_V2, type ForYouV2Event, type ForYouV2State } from '../utils/forYouV2';
import { readNdjsonStream } from './ndjsonStream';

/** Stream `path`, folding each line into state with `apply`; `onUpdate` gets the state after every line.
 * Aborting the signal closes the stream, which stops the server judging.
 * Resolves with the final state; rejects if aborted or the request fails. */
async function streamState<S extends { done: boolean; error: string | null }, E>(
  path: string, initial: S, apply: (state: S, event: E) => S,
  { signal, onUpdate, endedEarly }: { signal: AbortSignal; onUpdate: (state: S) => void; endedEarly: string },
): Promise<S> {
  const response = await apiStreamOptionalAuth(path, signal);
  let state = initial;
  await readNdjsonStream<E>(response, (event) => {
    state = apply(state, event);
    onUpdate(state);
  });
  if (!state.done) {
    state = { ...state, done: true, error: endedEarly };
    onUpdate(state);
  }
  return state;
}

/** Build a saved feed v2 for this load, streaming. Once it finishes it is the feed's stored result (its exports). */
export function streamFeedV2(
  { feedId, hours, signal, onUpdate }: {
    feedId: string; hours: number; signal: AbortSignal; onUpdate: (state: FeedV2State) => void;
  },
): Promise<FeedV2State> {
  const params = new URLSearchParams({ hours: String(hours), reader: 'true' });
  return streamState(`/api/v1/feeds/${encodeURIComponent(feedId)}/stream?${params}`, INITIAL_STATE, applyEvent,
    { signal, onUpdate, endedEarly: 'The feed ended early; showing what was ranked.' });
}

/** Build an unsaved search as its feed would be built, streaming. Nothing is stored. */
export function streamSearchV2(
  { query, hours, signal, onUpdate }: {
    query: string; hours: number; signal: AbortSignal; onUpdate: (state: FeedV2State) => void;
  },
): Promise<FeedV2State> {
  const params = new URLSearchParams({ q: query, hours: String(hours), reader: 'true' });
  return streamState(`/api/v1/feeds/search/stream?${params}`, INITIAL_STATE, applyEvent,
    { signal, onUpdate, endedEarly: 'The search ended early; showing what was ranked.' });
}

/** Build the reader's For You v2 (every feed shown in For You, combined), streaming. */
export function streamForYouV2(
  { hours, signal, onUpdate }: { hours: number; signal: AbortSignal; onUpdate: (state: ForYouV2State) => void },
): Promise<ForYouV2State> {
  // patches: this page merges patched stories (`ForYouV2Patch`); a page loaded before it could not, so it is asked for.
  const params = new URLSearchParams({ hours: String(hours), reader: 'true', patches: 'true' });
  return streamForYouV2From(`/api/v1/feeds/for-you/stream?${params}`, { signal, onUpdate });
}

/** Fold a For You v2 stream at `path` (the reader's own, or an admin's build of a user's) into state. */
export function streamForYouV2From(
  path: string, { signal, onUpdate }: { signal: AbortSignal; onUpdate: (state: ForYouV2State) => void },
): Promise<ForYouV2State> {
  return streamState<ForYouV2State, ForYouV2Event>(path, INITIAL_FOR_YOU_V2, applyForYouV2Event,
    { signal, onUpdate, endedEarly: 'For You ended early; showing what was ranked.' });
}

/** Stories in each feed's last stored build for *hours*, by feed id; a feed never built is absent. Never builds. */
export function storedStoryCounts(feedIds: string[], hours: number): Promise<Record<string, number>> {
  const params = new URLSearchParams({ ids: feedIds.join(','), hours: String(hours) });
  return apiJson<Record<string, number>>(`/api/v1/feeds/counts?${params}`, { public: true });
}
